// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Card, Group, Stack, Text } from '@mantine/core';

import type { ShopNavigationRoute } from 'app/ModuleRoutes';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';

import classes from './SelectableCard.module.css';
import type { PageTitle } from './types';

type Props = {
  /** The title of the page the user is navigating from */
  from: PageTitle;
  /** The route the user is navigating to */
  to: ShopNavigationRoute;
  /** The title displayed on the card */
  title: string;
  /** The description displayed on the card */
  description: string;
  /** The optional right section displayed on the card */
  rightSection?: ReactNode;
};

/**
 * Renders a card component that is selectable and navigates to a specified route when clicked.
 *
 * Used on mobile pages to display a list of selectable items.
 */
export const SelectableCard = ({ from, to, title, description, rightSection }: Props) => {
  const navigate = useEvolveNavigate(from);

  return (
    <Card className={classes.selectableCard} onClick={() => navigate(to)} py="xs" flex="1 0 auto">
      <Group justify="space-between" align="center">
        <Stack gap={0}>
          <Text>{title}</Text>
          <Text fz="sm" c="dimmed">
            {description}
          </Text>
        </Stack>

        {rightSection}
      </Group>
    </Card>
  );
};
